<template>
  <ul class="file-tree">
    <li
      v-for="node in nodes"
      :key="node.path"
    >
      <!-- Folder row -->
      <div
        v-if="node.type === 'folder'"
        class="tree-row folder"
        :class="{ selected: selectedPath === node.path }"
        @click="toggle(node)"
      >
        <span class="tree-caret">{{ isOpen(node) ? '▾' : '▸' }}</span>
        <span class="tree-icon">📁</span>

        <!-- Inline rename for folders -->
        <input
          v-if="renamingPath === node.path"
          ref="renameInput"
          v-model="renameValue"
          class="rename-input"
          @keydown.enter.prevent="commitRename(node)"
          @keydown.escape.prevent="cancelRename"
          @click.stop
          @blur="commitRename(node)"
        >
        <span
          v-else
          class="tree-label"
        >{{ node.name }}</span>

        <span class="tree-count">{{ countFiles(node) }}</span>

        <button
          v-if="isSkipEligible(node)"
          class="skip-toggle"
          :class="{ 'skip-toggle-off': isSkipped(node) }"
          :title="isSkipped(node)
            ? 'Excluded from ingestion (like Rapporter) — click to include. Not the same as the live/not live status below: this only controls whether a re-ingest will pick this folder up at all.'
            : 'Included in ingestion — click to exclude, like Rapporter. Not the same as the live/not live status below: this only means a re-ingest WILL pick this folder up, not that it already has.'"
          @click.stop="$emit('toggle-skip', node.name)"
        >
          {{ isSkipped(node) ? 'excluded' : 'included' }}
        </button>

        <!-- Folder actions (shown on hover) -->
        <div
          class="tree-actions"
          @click.stop
        >
          <button
            class="tree-action"
            title="Upload a file into this folder"
            @click.stop="$emit('select-folder', node.path)"
          >
            ↑ Upload
          </button>
          <button
            class="tree-action"
            title="Create a new subfolder here"
            @click.stop="$emit('create-folder', node.path)"
          >
            + Folder
          </button>
          <button
            class="tree-action"
            title="Rename this folder"
            @click.stop="startRename(node)"
          >
            Rename
          </button>
          <button
            class="tree-action"
            title="Move this folder"
            @click.stop="$emit('move-item', node)"
          >
            Move
          </button>
          <button
            class="tree-action tree-action-danger"
            title="Delete this folder and everything in it"
            @click.stop="$emit('delete-item', node)"
          >
            Delete
          </button>
        </div>
      </div>

      <!-- File row -->
      <div
        v-else
        class="tree-row file"
      >
        <span class="tree-caret" />
        <span class="tree-icon">{{ fileIcon(node.name) }}</span>

        <!-- Inline rename for files -->
        <input
          v-if="renamingPath === node.path"
          ref="renameInput"
          v-model="renameValue"
          class="rename-input"
          @keydown.enter.prevent="commitRename(node)"
          @keydown.escape.prevent="cancelRename"
          @click.stop
          @blur="commitRename(node)"
        >
        <span
          v-else
          class="tree-label"
          :class="{ 'tree-label-locked': !isRenamable(node) }"
          :title="!isRenamable(node) ? 'Baseline document name cannot be edited' : ''"
        >{{ node.name }}</span>

        <span
          v-if="node.source === 'upload'"
          class="src-badge src-upload"
          title="Uploaded (persistent volume)"
        >uploaded</span>

        <span
          class="live-badge"
          :class="node.indexed ? 'live-yes' : 'live-no'"
          :title="node.indexed
            ? 'Indexed in the vector database — searchable in chat right now'
            : 'Not indexed — saved to disk but not searchable in chat'"
        >{{ node.indexed ? 'live' : 'not live' }}</span>

        <!-- File actions (shown on hover) -->
        <div class="tree-actions">
          <a
            class="tree-action"
            :href="downloadUrl(node.path)"
            :download="node.name"
            title="Download this file"
            @click.stop
          >
            ↓ Download
          </a>
          <button
            v-if="isRenamable(node)"
            class="tree-action"
            title="Rename this file"
            @click.stop="startRename(node)"
          >
            Rename
          </button>
          <button
            v-else
            class="tree-action tree-action-disabled"
            title="Baseline document name cannot be edited"
            disabled
            @click.stop
          >
            Rename
          </button>
          <button
            class="tree-action"
            title="Move this file"
            @click.stop="$emit('move-item', node)"
          >
            Move
          </button>
          <button
            class="tree-action tree-action-danger"
            title="Delete this file"
            @click.stop="$emit('delete-item', node)"
          >
            Delete
          </button>
        </div>
      </div>

      <!-- Children (recurse) -->
      <file-tree
        v-if="node.type === 'folder' && isOpen(node)"
        :nodes="node.children"
        :selected-path="selectedPath"
        :open-paths="openPaths"
        :skip-info="skipInfo"
        @select-folder="$emit('select-folder', $event)"
        @toggle="$emit('toggle', $event)"
        @rename-item="$emit('rename-item', $event)"
        @move-item="$emit('move-item', $event)"
        @create-folder="$emit('create-folder', $event)"
        @delete-item="$emit('delete-item', $event)"
        @toggle-skip="$emit('toggle-skip', $event)"
      />
    </li>
  </ul>
</template>

<script>
import { downloadFileUrl } from '../api'

export default {
  name: 'FileTree',

  props: {
    nodes: { type: Array, required: true },
    selectedPath: { type: String, default: '' },
    openPaths: { type: Object, required: true },
    // { corpusPrefix: 'football/knowledge', skipDirs: ['Rapporter', …] } — which
    // top-level folder(s) under the current agent's own corpus are excluded from
    // the search index. Only a DIRECT child of corpusPrefix is toggle-eligible:
    // skip_dirs only ever matches a folder's top-level name (rag/ingest.py).
    skipInfo: { type: Object, default: () => ({ corpusPrefix: '', skipDirs: [] }) }
  },

  data () {
    return {
      renamingPath: '',
      renameValue: ''
    }
  },

  methods: {
    // Baseline documents ship with the app and are referenced by the pipeline —
    // only uploaded files can have their name changed.
    isRenamable (node) {
      return node.source === 'upload'
    },
    isOpen (node) {
      return !!this.openPaths[node.path]
    },
    isSkipEligible (node) {
      const prefix = this.skipInfo && this.skipInfo.corpusPrefix
      return !!prefix && node.path === `${prefix}/${node.name}`
    },
    isSkipped (node) {
      const skipDirs = (this.skipInfo && this.skipInfo.skipDirs) || []
      return skipDirs.includes(node.name)
    },
    toggle (node) {
      this.$emit('toggle', node.path)
    },
    countFiles (node) {
      let n = 0
      const walk = (items) => items.forEach(it => {
        if (it.type === 'file') n++
        else walk(it.children)
      })
      walk(node.children)
      return n
    },
    fileIcon (name) {
      const ext = name.split('.').pop().toLowerCase()
      if (ext === 'pdf') return '📕'
      if (ext === 'docx' || ext === 'doc') return '📘'
      if (ext === 'md') return '📝'
      if (ext === 'pptx' || ext === 'ppt') return '📙'
      return '📄'
    },
    downloadUrl (filePath) {
      return downloadFileUrl(filePath)
    },
    startRename (node) {
      this.renamingPath = node.path
      this.renameValue = node.name
      this.$nextTick(() => {
        const el = this.$refs.renameInput
        if (el) {
          const input = Array.isArray(el) ? el[0] : el
          if (input) {
            input.focus()
            input.select()
          }
        }
      })
    },
    commitRename (node) {
      const newName = (this.renameValue || '').trim()
      if (newName && newName !== node.name) {
        this.$emit('rename-item', { node, newName })
      }
      this.renamingPath = ''
      this.renameValue = ''
    },
    cancelRename () {
      this.renamingPath = ''
      this.renameValue = ''
    }
  }
}
</script>

<style scoped>
.file-tree {
  list-style: none;
  margin: 0;
  padding-left: 1.6rem;
}
.file-tree > li {
  margin: 0;
}
.tree-row {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.4rem 0.6rem;
  border-radius: 4px;
  font-size: 1.3rem;
  position: relative;
}
.tree-row.folder {
  cursor: pointer;
}
.tree-row.folder:hover,
.tree-row.file:hover {
  background: #f3f6f4;
}
.tree-row.folder.selected {
  background: #eaf1ec;
}
.tree-caret {
  width: 1.2rem;
  color: #8a9a90;
  font-size: 1.1rem;
  flex-shrink: 0;
}
.tree-icon {
  font-size: 1.3rem;
  flex-shrink: 0;
}
.tree-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #2b3a33;
}
.rename-input {
  flex: 1;
  min-width: 0;
  font-size: 1.3rem;
  border: 1px solid #46634f;
  border-radius: 3px;
  padding: 0.1rem 0.4rem;
  color: #2b3a33;
  background: #fff;
  outline: none;
}
.tree-count {
  font-size: 1.1rem;
  color: #8a9a90;
  flex-shrink: 0;
}

/* Action buttons — hidden until row hover */
.tree-actions {
  display: none;
  align-items: center;
  gap: 0.4rem;
  flex-shrink: 0;
}
.tree-row:hover .tree-actions {
  display: flex;
}
.tree-action {
  border: 0.5px solid #46634f;
  background: #f1ffe6;
  color: #46634f;
  border-radius: 4px;
  font-size: 1.05rem;
  padding: 0.15rem 0.55rem;
  cursor: pointer;
  text-decoration: none;
  white-space: nowrap;
}
.tree-action:hover {
  background: #e0f5e6;
}
.tree-action-disabled,
.tree-action-disabled:hover {
  opacity: 0.5;
  background: #f3f6f4;
  color: #8a9a90;
  border-color: #c7d1cb;
  cursor: not-allowed;
}
.tree-action-danger {
  border-color: #c0392b;
  background: #fdecea;
  color: #c0392b;
}
.tree-action-danger:hover {
  background: #fbdad6;
}

/* In-index / excluded toggle — same idea as the 'Rapporter' exclusion, made
   generic and clickable. Always visible (not hover-gated) so the exclusion
   state reads at a glance while browsing the tree. */
.skip-toggle {
  border: 0.5px solid #08a133;
  background: #f1ffe6;
  color: #08a133;
  border-radius: 4px;
  font-size: 1.05rem;
  padding: 0.15rem 0.55rem;
  cursor: pointer;
  flex-shrink: 0;
}
.skip-toggle:hover {
  background: #e0f5e6;
}
.skip-toggle-off {
  border-color: #b7c4cd;
  background: #eef2f5;
  color: #57707f;
}
.skip-toggle-off:hover {
  background: #e3e9ec;
}
.tree-label-locked {
  opacity: 0.65;
}

.src-badge {
  font-size: 1.05rem;
  padding: 0.1rem 0.5rem;
  border-radius: 4px;
  border: 0.5px solid transparent;
  flex-shrink: 0;
}
.src-upload {
  background: #f1ffe6;
  border-color: #08a133;
  color: #08a133;
}

/* Ground-truth indexed status — see AdminDashboard.vue's admin_files() call and
   its matching `.live-badge` rules for the org-wide "Not yet live" panel. */
.live-badge {
  font-size: 1.05rem;
  padding: 0.1rem 0.5rem;
  border-radius: 4px;
  border: 0.5px solid transparent;
  flex-shrink: 0;
}
.live-yes {
  background: #f1ffe6;
  border-color: #08a133;
  color: #08a133;
}
.live-no {
  background: #fff4ea;
  border-color: #eb6200;
  color: #eb6200;
}
</style>
